import { z } from 'zod'
import {
  DYEBATH_AGEING,
  DYE_SOURCE_TYPES,
  RECORD_KINDS,
  RECORD_VISIBILITIES,
  VOCAB_KINDS,
} from './enums'

/** Admin-managed lists whose records store the chosen term's name rather than its id. */
const vocabName = z.string().trim().min(1).max(120).nullish()

/**
 * Wizard steps post partial data on every save, so every field is optional at the
 * transport layer. `experimentCompleteSchema` is what a record must satisfy before
 * it can leave `draft`, and it is the single definition of "required" that both the
 * completion percentage and the submit button rely on.
 */

const id = z.string().uuid()
const optionalId = id.nullish()

/** Trims, and turns the empty string into null so blank inputs do not become "". */
const text = z
  .string()
  .trim()
  .transform((value) => (value === '' ? null : value))
  .nullish()

const requiredText = (field: string, max = 300) =>
  z.string().trim().min(1, `${field} is required`).max(max)

/** HTML number inputs post strings; coerce, then reject NaN explicitly. */
const number = (field: string, min: number, max: number) =>
  z.coerce
    .number({ invalid_type_error: `${field} must be a number` })
    .min(min, `${field} must be at least ${min}`)
    .max(max, `${field} must be at most ${max}`)

const optionalNumber = (field: string, min: number, max: number) =>
  z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? null : value),
    number(field, min, max).nullable(),
  )

/** `YYYY-MM-DD`, the format both the date input and Postgres `date` agree on. */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a valid date')
const optionalIsoDate = z.preprocess(
  (value) => (value === '' || value === null || value === undefined ? null : value),
  isoDate.nullable(),
)

/** Ratio dye-fabric (liquor ratio) is recorded as written in the source, e.g. "20:1". */
const liquorRatio = z
  .string()
  .trim()
  .regex(/^\d+(\.\d+)?\s*:\s*\d+(\.\d+)?$/, 'Use a ratio such as 20:1')

const ph = (field: string) => number(field, 0, 14)
const optionalPh = (field: string) => optionalNumber(field, 0, 14)

export const generalInformationSchema = z.object({
  title: requiredText('Experiment title'),
  manuscriptId: optionalId,
  recipeNumber: text,
  experimentDate: isoDate,
  researcherName: requiredText('Researcher / author'),
  institutionId: optionalId,
  referenceId: optionalId,
  placeOfExperiment: text,
  notes: text,
})

export const textileSchema = z.object({
  texFiberId: id,
  texYarnTypeId: optionalId,
  texWeave: vocabName,
  texFabricWeight: optionalNumber('Fabric weight', 0, 5000),
  texSampleWeightDry: number('Sample weight (dry)', 0, 10000),
  texSampleDimensions: text,
  texPreTreatment: vocabName,
  texPreTreatmentDetails: text,
})

export const mordantingSchema = z.object({
  morWaterTypeId: id,
  morPhAdjustments: text,
  morRinseCount: optionalNumber('Rinsing after mordanting', 0, 50),
  morLiquorRatio: liquorRatio,
  morMaxTempC: optionalNumber('Max. temperature', -20, 300),
  morRinseWaterTypeId: optionalId,
  morStartingPh: ph('Starting pH'),
  morBoilingMin: optionalNumber('Boiling time', 0, 1440),
  morRinseTempC: optionalNumber('Rinsing temperature', -20, 300),
  morMordantIds: z.array(id).min(1, 'Select at least one mordant'),
  morMordantNotes: text,
  morSimmeringMin: optionalNumber('Simmering time', 0, 1440),
  morDryingAfter: vocabName,
  morConcentrationOwf: optionalNumber('Mordant concentration', 0, 1000),
})

export const dyeBathSchema = z.object({
  dyeSourceIds: z.array(id).min(1, 'Select at least one dye source'),
  dyeExtractionMethod: vocabName,
  dyeMaterialAmountOwf: optionalNumber('Dye material amount', 0, 1000),
  dyeWaterTypeId: id,
  dyeLiquorRatio: liquorRatio,
  dyeStartingPh: optionalPh('Starting pH'),
  dyePhAdjustments: text,
  dyeMaxTempC: number('Max. temperature', -20, 300),
  dyeBoilingMin: optionalNumber('Boiling time', 0, 1440),
  dyeSimmeringMin: optionalNumber('Simmering time', 0, 1440),
  dyeAgeing: z.enum(DYEBATH_AGEING).nullish(),
  dyeFiltration: vocabName,
})

export const aftertreatmentSchema = z.object({
  aftRinseCount: optionalNumber('Rinsing after dyeing', 0, 50),
  aftRinseWaterTypeId: optionalId,
  aftRinseTempC: optionalNumber('Rinsing temperature', -20, 300),
  aftFinalRinsePh: optionalPh('Final rinse pH'),
  aftDryingMethod: vocabName,
  aftDryingConditions: text,
})

export const resultsSchema = z.object({
  resLabL: number('CIELAB L*', 0, 100),
  resLabA: number('CIELAB a*', -128, 128),
  resLabB: number('CIELAB b*', -128, 128),
  resIlluminant: vocabName,
  resObserver: vocabName,
  resInstrument: text,
  resMeasurementDate: optionalIsoDate,
  resAdditionalNotes: text,
})

/** The seven wizard steps, in order. Step 7 is Review and has no fields of its own. */
export const EXPERIMENT_STEPS = [
  { key: 'general', title: 'General Information', description: 'Basic details about this experiment.', schema: generalInformationSchema },
  { key: 'textile', title: 'Textile & Materials', description: 'Describe the material being dyed.', schema: textileSchema },
  { key: 'mordanting', title: 'Mordanting', description: 'Mordant preparation and application.', schema: mordantingSchema },
  { key: 'dyebath', title: 'Dye Bath', description: 'Extraction and dyeing conditions.', schema: dyeBathSchema },
  { key: 'aftertreatment', title: 'Aftertreatment', description: 'Rinsing and drying after dyeing.', schema: aftertreatmentSchema },
  { key: 'results', title: 'Results & Analysis', description: 'Measured colour values and supporting data.', schema: resultsSchema },
  { key: 'review', title: 'Review', description: 'Check the details before submitting to the shared database.', schema: null },
] as const

export type ExperimentStepKey = (typeof EXPERIMENT_STEPS)[number]['key']

/** Everything a finished experiment must contain. */
export const experimentCompleteSchema = generalInformationSchema
  .merge(textileSchema)
  .merge(mordantingSchema)
  .merge(dyeBathSchema)
  .merge(aftertreatmentSchema)
  .merge(resultsSchema)

export type ExperimentInput = z.input<typeof experimentCompleteSchema>
export type ExperimentValues = z.output<typeof experimentCompleteSchema>

/** Same fields, all optional: what an in-progress draft is allowed to look like. */
export const experimentDraftSchema = experimentCompleteSchema.partial().extend({
  title: z.string().trim().max(300).nullish(),
})

export type ExperimentDraftInput = z.input<typeof experimentDraftSchema>

/**
 * References are catalogue entries for a historical source rather than a lab run,
 * so they carry provenance and a colour but none of the process detail.
 */
export const referenceSchema = z.object({
  title: requiredText('Reference title'),
  manuscriptId: optionalId,
  recipeNumber: text,
  researcherName: requiredText('Researcher / author'),
  institutionId: optionalId,
  referenceYear: optionalNumber('Reference year', 0, 2200),
  texFiberId: id,
  morMordantIds: z.array(id).min(1, 'Select at least one mordant'),
  morMordantNotes: text,
  dyeSourceIds: z.array(id).min(1, 'Select at least one dye source'),
  resLabL: optionalNumber('CIELAB L*', 0, 100),
  resLabA: optionalNumber('CIELAB a*', -128, 128),
  resLabB: optionalNumber('CIELAB b*', -128, 128),
  connectedExperimentIds: z.array(id).default([]),
  notes: text,
})

export type ReferenceInput = z.input<typeof referenceSchema>

/**
 * The first problem with a single value as it is being typed, e.g. letters in a
 * number field. Empty values pass: missing required fields are reported on finish,
 * not while the researcher is still working through the form.
 */
export function fieldValueError(
  kind: 'experiment' | 'reference',
  field: string,
  value: unknown,
): string | undefined {
  const empty =
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0)
  if (empty) return undefined

  const shape: z.ZodRawShape =
    kind === 'reference' ? referenceSchema.shape : experimentCompleteSchema.shape
  const result = shape[field]?.safeParse(value)
  return result && !result.success ? result.error.issues[0]?.message : undefined
}

/** Master data */

export const dyeSourceSchema = z.object({
  canonicalName: requiredText('Common name', 120),
  scientificName: requiredText('Scientific name', 120),
  sourceType: z.enum(DYE_SOURCE_TYPES).nullish(),
  regionOfOrigin: text,
  typicalHue: text,
  swatchHex: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour such as #9b2c21')
    .nullish(),
})

export const vocabTermSchema = z.object({
  kind: z.enum(VOCAB_KINDS),
  name: requiredText('Name', 120),
  attrs: z.record(z.string()).default({}),
})

export const manuscriptSchema = z.object({
  name: requiredText('Manuscript name', 200),
  year: optionalNumber('Year', 0, 2200),
  holdingInstitution: text,
  notes: text,
})

/** Workspaces, review and sharing */

export const workspaceSchema = z.object({
  name: requiredText('Workspace name', 120),
  description: text,
})

export const inviteSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email address'),
})

export const reviewSchema = z
  .object({
    decision: z.enum(['approve', 'reject']),
    reason: text,
  })
  .refine((value) => value.decision === 'approve' || Boolean(value.reason), {
    message: 'A reason is required when rejecting a submission',
    path: ['reason'],
  })

export const visibilitySchema = z.object({
  visibility: z.enum(RECORD_VISIBILITIES),
  workspaceIds: z.array(id).default([]),
})

export const recordKindSchema = z.enum(RECORD_KINDS)

/** Auth */

export const passwordSchema = z
  .string()
  .min(10, 'Minimum of 10 characters in length')
  .regex(/[A-Z]/, 'Contain at least one uppercase letter (A-Z)')
  .regex(/[0-9!@#$%^&*(),.?":{}|<>_\-+=[\]\\/~`';]/, 'Contain at least one numeric digit (0-9) or special symbol')

export const signUpAccountSchema = z
  .object({
    email: z.string().trim().toLowerCase().email('Enter a valid email address'),
    password: passwordSchema,
    confirmPassword: z.string(),
    acknowledged: z.literal(true, {
      errorMap: () => ({ message: 'Please acknowledge the Terms of Use and Research Data Policy' }),
    }),
  })
  .refine((value) => value.password === value.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export const signUpProfileSchema = z.object({
  firstName: requiredText('First name', 80),
  lastName: requiredText('Last name', 80),
})

export const signInSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
  rememberMe: z.boolean().default(false),
})

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((value) => value.newPassword === value.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export const profileSchema = z.object({
  firstName: requiredText('First name', 80),
  lastName: requiredText('Last name', 80),
})
